import { EQuoteSlaType, IQuote } from 'types/quote.types';

const isExpired = (expirationDateISO?: string) => {
  if (!expirationDateISO) return false;

  const now = new Date();
  const expiration = new Date(expirationDateISO);

  return now > expiration;
};

export const convertMsToTimeParts = (ms: number) => {
  const MS_PER_MIN = 60000;
  const MS_PER_HOUR = 3600000;
  const MS_PER_DAY = 86400000;

  const days = Math.floor(ms / MS_PER_DAY);
  const hours = Math.floor((ms % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((ms % MS_PER_HOUR) / MS_PER_MIN);

  const text = days > 0 ? `${days}d ${hours}h ${minutes}m` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

  return { days, hours, minutes, text };
};

/**
 * Calcula el tiempo transcurrido en milisegundos excluyendo fines de semana
 */
export const calcBusinessTimeUTC = (dateOne: string, dateTwo: string) => {
  if (!dateOne || !dateTwo) return 0;

  let start = new Date(dateOne);
  let end = new Date(dateTwo);

  if (start > end) {
    const temp = start;
    start = end;
    end = temp;
  }

  // Constantes de tiempo
  const MS_PER_SECOND = 1000;
  const MS_PER_MINUTE = 60 * MS_PER_SECOND;
  const MS_PER_HOUR = 60 * MS_PER_MINUTE;
  const MS_PER_DAY = 24 * MS_PER_HOUR;

  // 1. Normalizar fechas a medianoche UTC
  const startMidnight = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const endMidnight = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());

  const totalDays = Math.floor((endMidnight - startMidnight) / MS_PER_DAY);
  const fullWeeks = Math.floor(totalDays / 7);

  // 2. Tiempo base (5 días laborables por semana)
  let businessDays = fullWeeks * 5;

  // 3. Días restantes tras semanas completas
  let remainingDays = totalDays % 7;
  let currentDay = start.getUTCDay();

  for (let i = 0; i < remainingDays; i++) {
    if (currentDay !== 6 && currentDay !== 0) {
      businessDays++;
    }
    currentDay = (currentDay + 1) % 7;
  }

  let totalMs = businessDays * MS_PER_DAY;

  const startDay = start.getUTCDay();
  const endDay = end.getUTCDay();

  if (startDay !== 0 && startDay !== 6) {
    const msPassedInStartDay =
      start.getUTCHours() * MS_PER_HOUR +
      start.getUTCMinutes() * MS_PER_MINUTE +
      start.getUTCSeconds() * MS_PER_SECOND +
      start.getUTCMilliseconds();
    totalMs -= msPassedInStartDay;
  }

  if (endDay !== 0 && endDay !== 6) {
    const msPassedInEndDay =
      end.getUTCHours() * MS_PER_HOUR +
      end.getUTCMinutes() * MS_PER_MINUTE +
      end.getUTCSeconds() * MS_PER_SECOND +
      end.getUTCMilliseconds();
    totalMs += msPassedInEndDay;
  }

  return totalMs;
};

export const formatName = ({ firstName, lastName }: { firstName?: string; lastName?: string } = {}) => {
  return firstName && lastName ? `${firstName} ${lastName}` : '';
};

export const formatQuote = (quote: IQuote) => {
  const procurementTime = quote.slas?.find(sla => sla?.slaTypeCode === EQuoteSlaType.PROCUREMENT_RESPONSE_TIME);
  const clientTime = quote.slas?.find(sla => sla?.slaTypeCode === EQuoteSlaType.CLIENT_RESPONSE_TIME);

  const slaToApply = clientTime ?? procurementTime;

  const expirationDate = slaToApply?.expiresAt;
  const seventyPercentAt = slaToApply?.seventyPercentAt;

  return {
    ...quote,
    isExpired: isExpired(expirationDate),
    expiresSoon: isExpired(seventyPercentAt)
  };
};
